/**
 * Configuración de la empresa.
 *
 * La pestaña que justifica esta pantalla es **Preparación**: enseña qué
 * falta para poder emitir un acta o una factura conformes, con el artículo
 * de cada carencia y **qué deja de funcionar**.
 *
 * Hasta ahora eso era una página del manual y una sorpresa el día que
 * alguien intentaba cerrar la primera revisión. La diferencia entre
 * «faltan datos» y «sin el número de mantenedora, el acta que emita hoy no
 * cumple el anexo II §5.a.7» es la diferencia entre un aviso que se ignora
 * y uno que se atiende.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { DetailShell, type Tab } from './detail/detail-shell.js';
import { useForm, type UseFormResult } from '../forms/use-form.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';
import { isoDate, maxLength, required, type FieldRules } from '../forms/form-state.js';
import { AttachmentsPanel, type AttachmentItem } from '../forms/attachments-panel.js';
import { AccountingSettings } from './forms/accounting-settings.js';

/**
 * Comunidades autónomas y ciudades con estatuto de autonomía.
 *
 * Esta lista sí vive en el código, a diferencia de los sistemas PCI, que
 * vienen del catálogo del motor normativo. La razón es que son divisiones
 * administrativas del Estado, no reglas reglamentarias: no cambian con cada
 * modificación del RIPCI y no las versiona nadie. Lo que sí importa es que
 * el código no se teclee, porque el acta declara la CCAA que habilita
 * (anexo II §5.a.7) y un error ahí queda por escrito.
 */
const REGIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'AN', label: 'Andalucía' },
  { value: 'AR', label: 'Aragón' },
  { value: 'AS', label: 'Asturias' },
  { value: 'CB', label: 'Cantabria' },
  { value: 'CE', label: 'Ceuta' },
  { value: 'CL', label: 'Castilla y León' },
  { value: 'CM', label: 'Castilla-La Mancha' },
  { value: 'CN', label: 'Canarias' },
  { value: 'CT', label: 'Cataluña' },
  { value: 'EX', label: 'Extremadura' },
  { value: 'GA', label: 'Galicia' },
  { value: 'IB', label: 'Illes Balears' },
  { value: 'MC', label: 'Región de Murcia' },
  { value: 'MD', label: 'Madrid' },
  { value: 'ML', label: 'Melilla' },
  { value: 'NC', label: 'Navarra' },
  { value: 'PV', label: 'País Vasco' },
  { value: 'RI', label: 'La Rioja' },
  { value: 'VC', label: 'Comunitat Valenciana' },
];

const AUTHORIZATION_RULES: FieldRules = {
  systemCode: [required('El sistema')],
  regionCode: [required('La comunidad autónoma')],
  grantedOn: [required('La fecha de concesión'), isoDate()],
  expiresOn: [isoDate()],
  registryNo: [maxLength(64)],
};

interface Profile {
  id: string;
  tax_id: string;
  legal_name: string;
  trade_name: string | null;
  address: Record<string, string>;
  email: string | null;
  phone: string | null;
  maintainer_reg_no: string | null;
  label_address: string | null;
  label_contact: string | null;
  installer_reg_no: string | null;
  liability_insurer: string | null;
  liability_policy_no: string | null;
  liability_expires_on: string | null;
  verifactu_mode: string;
}

interface Issue {
  code: string;
  severity: 'BLOQUEANTE' | 'RECOMENDADO';
  title: string;
  consequence: string;
  articleRef: string;
}

interface Authorization {
  id: string;
  role_type: string;
  system_code: string;
  region_code: string;
  registry_no: string | null;
  granted_on: string;
  expires_on: string | null;
  is_active: boolean;
  expired: boolean;
}

interface Qualification {
  id: string;
  full_name: string;
  system_code: string;
  certificate_no: string | null;
  granted_on: string;
  expires_on: string | null;
  expired: boolean;
}

const RULES: FieldRules = {
  legalName: [required('La razón social'), maxLength(255)],
  taxId: [required('El NIF'), maxLength(20)],
  liabilityExpiresOn: [isoDate()],
};

export function CompanySettingsScreen(): React.ReactNode {
  const { api } = useSession();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [readiness, setReadiness] = useState<{ ready: boolean; issues: Issue[] } | null>(null);
  const [authorizations, setAuthorizations] = useState<Authorization[]>([]);
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [systems, setSystems] = useState<{ code: string; name: string }[]>([]);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [users, setUsers] = useState<{ id: string; full_name: string }[]>([]);
  const qualFormRef = useRef<UseFormResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const authFormRef = useRef<UseFormResult | null>(null);

  const reload = useCallback(async () => {
    const [next, check, auths, quals, sys, people] = await Promise.all([
      api.get<Profile>('/company'),
      api.get<{ ready: boolean; issues: Issue[] }>('/company/readiness'),
      api.get<{ items: Authorization[] }>('/company/authorizations').catch(() => ({ items: [] })),
      api.get<{ items: Qualification[] }>('/company/qualifications').catch(() => ({ items: [] })),
      // Si aún no se ha publicado ninguna versión normativa no hay sistemas
      // que ofrecer. Se degrada a lista vacía y el formulario lo explica, en
      // vez de romper la pantalla entera.
      api
        .get<{ items: { code: string; name: string }[] }>('/regulations/systems')
        .catch(() => ({ items: [] })),
      // Sin `identity.read` no se pueden listar: el formulario lo explica en
      // vez de romperse.
      api
        .get<{ items: { id: string; full_name: string }[] }>('/users')
        .catch(() => ({ items: [] })),
    ]);
    setProfile(next);
    setReadiness(check);
    setAuthorizations(auths.items);
    setQualifications(quals.items);
    setSystems(sys.items);
    setUsers(people.items);

    // Va después y no en el `Promise.all`: los adjuntos cuelgan del id de la
    // empresa, que sale de esta misma respuesta.
    const files = await api
      .get<{ items: AttachmentItem[] }>(`/attachments/COMPANY/${next.id}`)
      .catch(() => ({ items: [] }));
    setAttachments(files.items);
  }, [api]);

  useEffect(() => {
    let cancelled = false;

    reload()
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(
            caught instanceof ApiError ? caught.message : 'No se ha podido cargar la configuración.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reload]);

  const form = useForm<Profile>({
    mode: 'edit',
    rules: RULES,
    nullable: ['tradeName', 'email', 'phone', 'maintainerRegNo', 'installerRegNo', 'liabilityExpiresOn', 'labelAddress', 'labelContact'],
    initial: {
      legalName: profile?.legal_name ?? '',
      tradeName: profile?.trade_name ?? '',
      taxId: profile?.tax_id ?? '',
      email: profile?.email ?? '',
      phone: profile?.phone ?? '',
      street: profile?.address?.['street'] ?? '',
      city: profile?.address?.['city'] ?? '',
      postalCode: profile?.address?.['postalCode'] ?? '',
      province: profile?.address?.['province'] ?? '',
      maintainerRegNo: profile?.maintainer_reg_no ?? '',
      labelAddress: profile?.label_address ?? '',
      labelContact: profile?.label_contact ?? '',
      installerRegNo: profile?.installer_reg_no ?? '',
      liabilityInsurer: profile?.liability_insurer ?? '',
      liabilityPolicyNo: profile?.liability_policy_no ?? '',
      liabilityExpiresOn: profile?.liability_expires_on ?? '',
    },
    onSubmit: async (body, client) => {
      // La dirección viaja como objeto: el art. 6.1.e habla del domicilio
      // completo, no de una cadena suelta.
      const { street, city, postalCode, province, ...rest } = body as Record<string, unknown>;
      const payload: Record<string, unknown> = { ...rest };

      if (street !== undefined || city !== undefined || postalCode !== undefined || province !== undefined) {
        payload['address'] = {
          street: street ?? profile?.address?.['street'] ?? '',
          city: city ?? profile?.address?.['city'] ?? '',
          postalCode: postalCode ?? profile?.address?.['postalCode'] ?? '',
          province: province ?? profile?.address?.['province'] ?? '',
          country: profile?.address?.['country'] ?? 'ES',
        };
      }

      return client.request<Profile>('/company', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
    onSaved: () => void reload(),
  });

  /**
   * Alta de habilitación.
   *
   * Faltaba: la pestaña listaba las habilitaciones y no había forma de crear
   * ninguna, mientras la comprobación de preparación exigía tener al menos
   * una. El sistema pedía un dato y no ofrecía dónde meterlo.
   */
  const authForm = useForm<void>({
    mode: 'create',
    rules: AUTHORIZATION_RULES,
    nullable: ['registryNo', 'expiresOn'],
    initial: {
      roleType: 'MAINTAINER',
      systemCode: '',
      regionCode: '',
      registryNo: '',
      grantedOn: '',
      expiresOn: '',
    },
    onSubmit: async (body, client) =>
      client.request<void>('/company/authorizations', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSaved: () => {
      // Se limpia el sistema y el nº de registro, pero se conservan papel,
      // CCAA y fecha: lo normal es dar de alta varias habilitaciones seguidas
      // de la misma comunidad y la misma resolución, y volver a elegirlas
      // cada vez serían tres campos de más por sistema.
      //
      // Se pasa por una referencia porque `onSaved` no puede leer `authForm`
      // dentro de la expresión que lo declara.
      const previous = authFormRef.current;
      previous?.reset({
        roleType: previous.values['roleType'] ?? 'MAINTAINER',
        systemCode: '',
        regionCode: previous.values['regionCode'] ?? '',
        registryNo: '',
        grantedOn: previous.values['grantedOn'] ?? '',
        expiresOn: '',
      });
      void reload();
    },
  });
  authFormRef.current = authForm;

  /**
   * Alta de cualificación.
   *
   * Mismo agujero que tenían las habilitaciones: la pestaña listaba y no
   * dejaba crear. Y el acta lo reclama —«no declara la cualificación de
   * Fulano para EXTINTORES»—, así que sin esto ningún acta es conforme.
   */
  const qualForm = useForm<void>({
    mode: 'create',
    rules: {
      userId: [required('El técnico')],
      systemCode: [required('El sistema')],
      grantedOn: [required('La fecha de concesión'), isoDate()],
      expiresOn: [isoDate()],
      certificateNo: [maxLength(64)],
    },
    nullable: ['certificateNo', 'expiresOn'],
    initial: { userId: '', systemCode: '', certificateNo: '', grantedOn: '', expiresOn: '' },
    onSubmit: async (body, client) =>
      client.request<void>('/company/qualifications', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSaved: () => {
      const previous = qualFormRef.current;
      // Se conserva el técnico y la fecha: lo normal es declararle varios
      // sistemas seguidos con la misma acreditación.
      previous?.reset({
        userId: previous.values['userId'] ?? '',
        systemCode: '',
        certificateNo: previous.values['certificateNo'] ?? '',
        grantedOn: previous.values['grantedOn'] ?? '',
        expiresOn: '',
      });
      void reload();
    },
  });
  qualFormRef.current = qualForm;

  const blocking = readiness?.issues.filter((i) => i.severity === 'BLOQUEANTE') ?? [];
  const advisory = readiness?.issues.filter((i) => i.severity === 'RECOMENDADO') ?? [];

  const tabs: Tab[] = [
    {
      id: 'preparacion',
      label: 'Preparación',
      count: blocking.length,
      render: () => (
        <>
          <p className={`screen__note${readiness?.ready === false ? ' screen__note--strong' : ''}`}>
            {readiness?.ready ? (
              <>
                ✓ La empresa puede emitir actas y facturas conformes. Lo que quede abajo son
                recomendaciones, no impedimentos.
              </>
            ) : (
              <>
                <strong>Todavía no se pueden emitir documentos conformes.</strong> Cada punto dice qué
                artículo se incumpliría y qué deja de funcionar.
              </>
            )}
          </p>

          {blocking.length > 0 && (
            <>
              <h2 className="screen__subtitle">Impiden emitir documentos conformes</h2>
              <ul className="alerts">
                {blocking.map((issue) => (
                  <li className="alerts__item" key={issue.code}>
                    <span className="pill pill--bad">⚠</span>
                    <div className="alerts__body">
                      <p className="alerts__message">{issue.title}</p>
                      {/* La consecuencia, no sólo el campo que falta: es lo
                          que convierte el aviso en algo que se atiende. */}
                      <p className="alerts__meta">
                        {issue.consequence} <em>({issue.articleRef})</em>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {advisory.length > 0 && (
            <>
              <h2 className="screen__subtitle">Recomendado</h2>
              <ul className="alerts">
                {advisory.map((issue) => (
                  <li className="alerts__item" key={issue.code}>
                    <span className="pill pill--warn">◷</span>
                    <div className="alerts__body">
                      <p className="alerts__message">{issue.title}</p>
                      <p className="alerts__meta">
                        {issue.consequence} <em>({issue.articleRef})</em>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ),
    },
    {
      id: 'datos',
      label: 'Datos',
      render: () => (
        <>
        <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
          {form.formError !== null && (
            <p className="screen__error" role="alert">
              {form.formError}
            </p>
          )}

          <FormSection title="Identificación">
            <TextField
              label="Razón social"
              value={String(form.values['legalName'] ?? '')}
              onChange={(v) => form.set('legalName', v)}
              onBlur={() => form.blur('legalName')}
              error={form.errors['legalName']}
              required
            />
            <TextField
              label="Nombre comercial"
              value={String(form.values['tradeName'] ?? '')}
              onChange={(v) => form.set('tradeName', v)}
            />
            <TextField
              label="NIF"
              value={String(form.values['taxId'] ?? '')}
              onChange={(v) => form.set('taxId', v)}
              onBlur={() => form.blur('taxId')}
              error={form.errors['taxId']}
              required
            />
            <TextField
              label="Correo"
              type="email"
              value={String(form.values['email'] ?? '')}
              onChange={(v) => form.set('email', v)}
            />
            <TextField
              label="Teléfono"
              type="tel"
              value={String(form.values['phone'] ?? '')}
              onChange={(v) => form.set('phone', v)}
            />
          </FormSection>

          <FormSection
            title="Domicilio fiscal"
            description="Va impreso en cada factura que emita (art. 6.1.e RD 1619/2012)."
          >
            <TextField
              label="Calle y número"
              value={String(form.values['street'] ?? '')}
              onChange={(v) => form.set('street', v)}
              required
            />
            <TextField
              label="Población"
              value={String(form.values['city'] ?? '')}
              onChange={(v) => form.set('city', v)}
              required
            />
            <TextField
              label="Código postal"
              value={String(form.values['postalCode'] ?? '')}
              onChange={(v) => form.set('postalCode', v)}
            />
            <TextField
              label="Provincia"
              value={String(form.values['province'] ?? '')}
              onChange={(v) => form.set('province', v)}
            />
          </FormSection>

          <FormSection
            title="Registro industrial"
            description="El número de mantenedora se declara en cada acta (anexo II §5.a.7 RD 513/2017)."
          >
            <TextField
              label="Nº como empresa mantenedora"
              value={String(form.values['maintainerRegNo'] ?? '')}
              onChange={(v) => form.set('maintainerRegNo', v)}
              required
            />
            <TextField
              label="Nº como empresa instaladora"
              value={String(form.values['installerRegNo'] ?? '')}
              onChange={(v) => form.set('installerRegNo', v)}
              help="Sólo si además instala."
            />
          </FormSection>

          <FormSection
            title="Etiqueta del equipo"
            description="Lo que se pega en el extintor mide cuarenta milímetros. Estos campos son sólo para eso: no cambian la dirección fiscal ni el teléfono de la ficha."
          >
            <TextField
              label="Dirección abreviada"
              value={String(form.values['labelAddress'] ?? '')}
              onChange={(v) => form.set('labelAddress', v)}
              maxLength={140}
              help="Si se deja vacía, se compone de la dirección fiscal y puede quedar cortada."
            />
            <TextField
              label="Línea de contacto"
              value={String(form.values['labelContact'] ?? '')}
              onChange={(v) => form.set('labelContact', v)}
              maxLength={140}
              help="Web y teléfono al que llama quien ve la etiqueta. Vacío = el nombre comercial."
            />
          </FormSection>

          <FormSection
            title="Seguro de responsabilidad civil"
            description="El art. 15 del RIPCI lo exige para ejercer como empresa mantenedora habilitada."
          >
            <TextField
              label="Aseguradora"
              value={String(form.values['liabilityInsurer'] ?? '')}
              onChange={(v) => form.set('liabilityInsurer', v)}
              required
            />
            <TextField
              label="Nº de póliza"
              value={String(form.values['liabilityPolicyNo'] ?? '')}
              onChange={(v) => form.set('liabilityPolicyNo', v)}
              required
            />
            <TextField
              label="Vencimiento"
              type="date"
              value={String(form.values['liabilityExpiresOn'] ?? '')}
              onChange={(v) => form.set('liabilityExpiresOn', v)}
              onBlur={() => form.blur('liabilityExpiresOn')}
              error={form.errors['liabilityExpiresOn']}
              help="El sistema avisa con 60 días de antelación."
            />
          </FormSection>

          <footer className="form__actions">
            <button className="button button--primary" type="submit" disabled={form.saving}>
              {form.saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </footer>
        </form>

        <AttachmentsPanel
          title="Logotipo"
          note="Sale en la cabecera de los albaranes y demás documentos que ve el cliente. PNG o JPEG; conviene fondo transparente o blanco. Si hay varios, se usa el último subido."
          entityType="COMPANY"
          entityId={profile?.id ?? null}
          kind="LOGO"
          items={attachments}
          onChanged={() => void reload()}
        />

        <AttachmentsPanel
          title="Logotipo para etiquetas (negro)"
          note="Las etiquetas se imprimen en soporte metálico y a un solo color: un logotipo en color sale como una mancha. Conviene una versión en negro, normalmente sólo la marca sin el símbolo. Si falta, se usa el de color."
          entityType="COMPANY"
          entityId={profile?.id ?? null}
          kind="LOGO_MONO"
          items={attachments}
          onChanged={() => void reload()}
        />

        <AttachmentsPanel
          title="Documentos de la empresa"
          note="La póliza de responsabilidad civil y cualquier papel que acredite lo declarado arriba. Una fecha de vencimiento sin el documento detrás no acredita nada ante una inspección (art. 15 RIPCI)."
          entityType="COMPANY"
          entityId={profile?.id ?? null}
          kind="LIABILITY_POLICY"
          items={attachments}
          onChanged={() => void reload()}
        />
        </>
      ),
    },
    {
      id: 'habilitaciones',
      label: 'Habilitaciones',
      count: authorizations.filter((a) => a.is_active).length,
      render: () => (
        <>
          <p className="screen__note">
            El acta declara para qué sistemas y en qué comunidad autónoma está habilitada la empresa
            (anexo II §5.a.7). <strong>Declarar una habilitación caducada es peor que no
            declararla</strong>: consta por escrito.
          </p>

          <form className="form" onSubmit={(e) => void authForm.submit(e)} noValidate>
            {authForm.formError !== null && (
              <p className="screen__error" role="alert">
                {authForm.formError}
              </p>
            )}

            <FormSection title="Añadir habilitación">
              <SelectField
                label="Papel"
                value={String(authForm.values['roleType'] ?? 'MAINTAINER')}
                onChange={(v) => authForm.set('roleType', v)}
                options={[
                  { value: 'MAINTAINER', label: 'Mantenedora' },
                  { value: 'INSTALLER', label: 'Instaladora' },
                ]}
                required
              />
              <SelectField
                label="Sistema"
                value={String(authForm.values['systemCode'] ?? '')}
                onChange={(v) => authForm.set('systemCode', v)}
                onBlur={() => authForm.blur('systemCode')}
                error={authForm.errors['systemCode']}
                options={systems.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }))}
                emptyLabel="Seleccione un sistema…"
                help={
                  systems.length === 0
                    ? 'No hay sistemas en el catálogo. Cargue y publique una versión normativa antes de declarar habilitaciones.'
                    : undefined
                }
                required
              />
              <SelectField
                label="Comunidad autónoma"
                value={String(authForm.values['regionCode'] ?? '')}
                onChange={(v) => authForm.set('regionCode', v)}
                onBlur={() => authForm.blur('regionCode')}
                error={authForm.errors['regionCode']}
                options={[...REGIONS]}
                emptyLabel="Seleccione una comunidad…"
                help="Las habilitaciones son territoriales: se declara una por cada comunidad donde se opera."
                required
              />
              <TextField
                label="Nº de registro"
                value={String(authForm.values['registryNo'] ?? '')}
                onChange={(v) => authForm.set('registryNo', v)}
                error={authForm.errors['registryNo']}
                help="El de la resolución que concede la habilitación, si lo tiene."
              />
              <TextField
                label="Concedida el"
                type="date"
                value={String(authForm.values['grantedOn'] ?? '')}
                onChange={(v) => authForm.set('grantedOn', v)}
                onBlur={() => authForm.blur('grantedOn')}
                error={authForm.errors['grantedOn']}
                required
              />
              <TextField
                label="Vence el"
                type="date"
                value={String(authForm.values['expiresOn'] ?? '')}
                onChange={(v) => authForm.set('expiresOn', v)}
                error={authForm.errors['expiresOn']}
                help="Vacío si no caduca. Una habilitación caducada bloquea la emisión del acta, no sólo avisa."
              />
              <button className="button button--primary" type="submit" disabled={authForm.saving}>
                {authForm.saving ? 'Añadiendo…' : 'Añadir habilitación'}
              </button>
            </FormSection>
          </form>

          {authorizations.length === 0 ? (
            <p className="screen__note">
              No hay ninguna habilitación declarada. Sin al menos una, las actas no cumplen el
              contenido mínimo.
            </p>
          ) : (
            <div className="scroller">
              <table className="table__grid table__grid--compact">
                <thead>
                  <tr>
                    <th>Sistema</th>
                    <th>CCAA</th>
                    <th>Papel</th>
                    <th>Nº registro</th>
                    <th>Concedida</th>
                    <th>Vence</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {authorizations.map((auth) => (
                    <tr key={auth.id}>
                      <td>{auth.system_code}</td>
                      <td>{auth.region_code}</td>
                      <td>{auth.role_type === 'MAINTAINER' ? 'Mantenedora' : 'Instaladora'}</td>
                      <td>{auth.registry_no ?? '—'}</td>
                      <td>{auth.granted_on}</td>
                      <td>{auth.expires_on ?? 'Sin caducidad'}</td>
                      <td>
                        {!auth.is_active ? (
                          <span className="pill pill--off">De baja</span>
                        ) : auth.expired ? (
                          <span className="pill pill--bad">⚠ Caducada</span>
                        ) : (
                          <span className="pill pill--ok">✓ Vigente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ),
    },
    {
      id: 'cualificaciones',
      label: 'Cualificaciones',
      count: qualifications.length,
      render: () => (
        <>
          <p className="screen__note">
            El acta declara quién ejecutó el mantenimiento y para qué está cualificado (anexo II
            §5.a.8).
          </p>

          <form className="form" onSubmit={(e) => void qualForm.submit(e)} noValidate>
            {qualForm.formError !== null && (
              <p className="screen__error" role="alert">
                {qualForm.formError}
              </p>
            )}

            <FormSection title="Declarar cualificación">
              <SelectField
                label="Técnico"
                value={String(qualForm.values['userId'] ?? '')}
                onChange={(v) => qualForm.set('userId', v)}
                onBlur={() => qualForm.blur('userId')}
                error={qualForm.errors['userId']}
                options={users.map((u) => ({ value: u.id, label: u.full_name }))}
                emptyLabel="Seleccione…"
                help={
                  users.length === 0
                    ? 'No hay usuarios que mostrar. Dé de alta técnicos en Configuración → Usuarios.'
                    : undefined
                }
                required
              />
              <SelectField
                label="Sistema"
                value={String(qualForm.values['systemCode'] ?? '')}
                onChange={(v) => qualForm.set('systemCode', v)}
                onBlur={() => qualForm.blur('systemCode')}
                error={qualForm.errors['systemCode']}
                options={systems.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }))}
                emptyLabel="Seleccione un sistema…"
                help="Una por cada sistema para el que esté acreditado. El acta las declara una a una."
                required
              />
              <TextField
                label="Nº de certificado"
                value={String(qualForm.values['certificateNo'] ?? '')}
                onChange={(v) => qualForm.set('certificateNo', v)}
                error={qualForm.errors['certificateNo']}
              />
              <TextField
                label="Concedida el"
                type="date"
                value={String(qualForm.values['grantedOn'] ?? '')}
                onChange={(v) => qualForm.set('grantedOn', v)}
                onBlur={() => qualForm.blur('grantedOn')}
                error={qualForm.errors['grantedOn']}
                required
              />
              <TextField
                label="Vence el"
                type="date"
                value={String(qualForm.values['expiresOn'] ?? '')}
                onChange={(v) => qualForm.set('expiresOn', v)}
                error={qualForm.errors['expiresOn']}
                help="Vacío si no caduca."
              />
              <button className="button button--primary" type="submit" disabled={qualForm.saving}>
                {qualForm.saving ? 'Declarando…' : 'Declarar cualificación'}
              </button>
            </FormSection>
          </form>

          {qualifications.length === 0 ? (
            <p className="screen__note">Ningún técnico tiene cualificaciones declaradas.</p>
          ) : (
            <div className="scroller">
              <table className="table__grid table__grid--compact">
                <thead>
                  <tr>
                    <th>Técnico</th>
                    <th>Sistema</th>
                    <th>Certificado</th>
                    <th>Concedida</th>
                    <th>Vence</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {qualifications.map((qual) => (
                    <tr key={qual.id}>
                      <td>{qual.full_name}</td>
                      <td>{qual.system_code}</td>
                      <td>{qual.certificate_no ?? '—'}</td>
                      <td>{qual.granted_on}</td>
                      <td>{qual.expires_on ?? 'Sin caducidad'}</td>
                      <td>
                        {qual.expired ? (
                          <span className="pill pill--bad">⚠ Caducada</span>
                        ) : (
                          <span className="pill pill--ok">✓ Vigente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ),
    },
    {
      id: 'contabilidad',
      label: 'Contabilidad',
      render: () => <AccountingSettings />,
    },
  ];

  return (
    <DetailShell
      title="Configuración de la empresa"
      subtitle={profile ? `${profile.legal_name} · ${profile.tax_id}` : undefined}
      badges={
        readiness &&
        (readiness.ready ? (
          <span className="pill pill--ok">✓ Lista para emitir</span>
        ) : (
          <span className="pill pill--bad">⚠ {blocking.length} carencia(s) bloqueante(s)</span>
        ))
      }
      tabs={tabs}
      loading={loading}
      error={error}
    />
  );
}
